import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SxProps, Theme } from '@mui/material';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MobileCardList } from './MobileCardList';
import { TableSkeleton } from './skeletons';

type SortDirection = 'asc' | 'desc';

interface DataTableColumn<T> {
  header: string;
  renderCell: (item: T, index: number) => React.ReactNode;
  sortKey?: string;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
}

interface QuickFilter {
  label: string;
  value: string;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  getRowKey: (item: T) => string;

  // Uncontrolled sort
  sortCompareFn?: (a: T, b: T, sortKey: string) => number;
  defaultSortKey?: string;
  defaultSortDirection?: SortDirection;
  // Controlled sort
  sortKey?: string;
  sortDirection?: SortDirection;
  onSortChange?: (sortKey: string, direction: SortDirection) => void;

  // Quick filters
  quickFilters?: QuickFilter[];
  activeFilter?: string;
  onFilterChange?: (value: string) => void;

  // Mobile
  renderMobileCard: (item: T, index: number) => React.ReactNode;

  // Interactions
  /** `event` is set for mouse clicks so callers can honour Ctrl/Cmd+click (#3653). */
  onRowClick?: (item: T, event?: React.MouseEvent) => void;
  /** Row to highlight, compared with `getRowKey`, e.g. the one a second window shows (#3744). */
  selectedRowKey?: string | null;

  // States
  loading?: boolean;
  emptyMessage?: string;
  /**
   * Optional guidance shown under `emptyMessage` (#2726).
   *
   * Most lists answered an empty table with a bare noun phrase — "Ei
   * laskuja" — which tells the user what is missing but not what to do.
   * Both are optional, so the 19 existing call sites are unaffected.
   */
  emptyDescription?: string;
  /** Optional call to action for the empty state, e.g. "Luo ensimmäinen". */
  emptyAction?: { label: string; onClick: () => void };

  // Customization
  ariaLabel?: string;
  sx?: SxProps<Theme>;
  paperWrapper?: boolean;
}

function DataTable<T>({
  columns,
  data,
  getRowKey,
  sortCompareFn,
  defaultSortKey,
  defaultSortDirection = 'asc',
  sortKey: controlledSortKey,
  sortDirection: controlledSortDirection,
  onSortChange,
  quickFilters,
  activeFilter,
  onFilterChange,
  renderMobileCard,
  onRowClick,
  selectedRowKey = null,
  loading = false,
  emptyMessage,
  emptyDescription,
  emptyAction,
  ariaLabel,
  sx,
  paperWrapper = true,
}: DataTableProps<T>) {
  const { t: tc } = useTranslation('common');
  const isMobile = useIsMobile();

  // Uncontrolled sort state
  const [internalSortKey, setInternalSortKey] = useState(defaultSortKey ?? '');
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>(defaultSortDirection);

  const isControlled = controlledSortKey !== undefined;
  const activeSortKey = isControlled ? controlledSortKey : internalSortKey;
  const activeSortDirection = isControlled ? (controlledSortDirection ?? 'asc') : internalSortDirection;

  const handleSortClick = (key: string) => {
    if (isControlled) {
      const newDirection: SortDirection = activeSortKey === key && activeSortDirection === 'asc' ? 'desc' : 'asc';
      onSortChange?.(key, newDirection);
    } else {
      if (internalSortKey === key) {
        setInternalSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setInternalSortKey(key);
        setInternalSortDirection('asc');
      }
    }
  };

  // Sort data for uncontrolled mode
  const sortedData = useMemo(() => {
    if (isControlled || !sortCompareFn || !activeSortKey) return data;

    return [...data].sort((a, b) => {
      const comparison = sortCompareFn(a, b, activeSortKey);
      return activeSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, isControlled, sortCompareFn, activeSortKey, activeSortDirection]);

  if (loading) {
    return <TableSkeleton columns={columns.length} rows={5} />;
  }

  /*
    One empty state for both layouts (#2726). Mobile and desktop each had a
    byte-identical copy, so a change had to be made twice or they drifted —
    the same trap as the template tabs in #2624.

    The clear-filters button stays separate from `emptyAction`: a list empty
    because of a filter needs a way back, which is not the same thing as the
    caller's "create the first one".
  */
  const emptyContent = (
    <Box sx={{ textAlign: 'center' }}>
      <Typography color="text.secondary">{emptyMessage || tc('noData')}</Typography>
      {emptyDescription && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, maxWidth: 420, mx: 'auto' }}
        >
          {emptyDescription}
        </Typography>
      )}
      {emptyAction && (
        <Button size="small" variant="outlined" onClick={emptyAction.onClick} sx={{ mt: 1.5 }}>
          {emptyAction.label}
        </Button>
      )}
      {activeFilter && activeFilter !== 'all' && onFilterChange && (
        <Button size="small" onClick={() => onFilterChange('all')} sx={{ mt: 1 }}>
          {tc('clearFilters')}
        </Button>
      )}
    </Box>
  );

  const quickFilterRow = quickFilters && quickFilters.length > 0 && (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
      {quickFilters.map((filter) => (
        <Chip
          key={filter.value}
          label={filter.label}
          color={activeFilter === filter.value ? 'primary' : 'default'}
          variant={activeFilter === filter.value ? 'filled' : 'outlined'}
          onClick={() => onFilterChange?.(filter.value)}
          size="small"
        />
      ))}
    </Stack>
  );

  // Mobile view
  if (isMobile) {
    return (
      <Box sx={sx}>
        {quickFilterRow}
        {sortedData.length === 0 ? (
          <Box sx={{ py: 4 }}>{emptyContent}</Box>
        ) : (
          <MobileCardList
            items={sortedData}
            onItemClick={onRowClick}
            renderCard={renderMobileCard}
          />
        )}
      </Box>
    );
  }

  // Desktop view
  const tableContent = (
    <Table aria-label={ariaLabel}>
      <TableHead>
        <TableRow>
          {columns.map((col, idx) => (
            <TableCell key={idx} align={col.align} width={col.width}>
              {col.sortKey ? (
                <TableSortLabel
                  active={activeSortKey === col.sortKey}
                  direction={activeSortKey === col.sortKey ? activeSortDirection : 'asc'}
                  onClick={() => handleSortClick(col.sortKey!)}
                >
                  {col.header}
                </TableSortLabel>
              ) : (
                col.header
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedData.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
              {emptyContent}
            </TableCell>
          </TableRow>
        ) : (
          sortedData.map((item, index) => {
            const rowKey = getRowKey(item);
            const selected = selectedRowKey !== null && rowKey === selectedRowKey;
            return (
              <TableRow
                key={rowKey}
                hover={!!onRowClick}
                selected={selected}
                aria-current={selected ? 'true' : undefined}
                sx={{
                  ...(onRowClick ? { cursor: 'pointer' } : {}),
                  '&.Mui-selected > td:first-of-type': {
                    boxShadow: (theme) => `inset 4px 0 0 ${theme.palette.primary.main}`,
                  },
                }}
                onClick={onRowClick ? (e: React.MouseEvent) => onRowClick(item, e) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'button' : undefined}
                onKeyDown={onRowClick ? (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(item);
                  }
                } : undefined}
              >
                {columns.map((col, colIdx) => (
                  <TableCell key={colIdx} align={col.align}>
                    {col.renderCell(item, index)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );

  return (
    <Box sx={sx}>
      {quickFilterRow}
      {paperWrapper ? (
        <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
          {tableContent}
        </TableContainer>
      ) : (
        <TableContainer>
          {tableContent}
        </TableContainer>
      )}
    </Box>
  );
}

export { DataTable, type DataTableColumn, type DataTableProps, type QuickFilter };
